import { useMemo, useState } from 'react';

import { NO_ANSWER_MS, useStalled } from '@/hooks/useStalled';
import { useLimuloPolling, useLimuloPollingGuida } from '@/hooks/useLimuloPolling';
import { LIMULO_POLL_LISTA_MS } from '@/lib/limulo/polling';
import {
  featuredTournamentOf,
  isMineTournament,
  torneiDelTipoOf,
} from '@/lib/qgammon/lobbyTornei';
import {
  LIMULO_PAGE_SIZE,
  hasNextPage,
  isPublished,
  nonEUnaSfida,
  sortTournaments,
} from '@/lib/limulo/model';
import {
  useGetLimuloGamesQuery,
  useGetLimuloTournamentsQuery,
  useGetMyLimuloTournamentsQuery,
} from '@/services/limuloApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { listaDelGioco, profileTypeDelGioco } from '@/lib/limulo/giocoSelezionato';
import type { LimuloTournament } from '@/types/limulo';

/**
 * Cosa puo' dire, oggi, un pannello che mostra i tornei di UN gioco (il suo `profileType` nel catalogo). **I cinque vuoti**
 * (CLAUDE.md del repo, «Gli 8 tab della Hub») hanno un nome ciascuno, e il quinto — «vuoto
 * davvero» — e' l'unico che dice «nessun torneo».
 */
export type TorneiDelGioco =
  /** 1 e 2: sto caricando, e `slow` = da troppo (una transizione, non un verdetto). */
  | { kind: 'waiting'; slow: boolean }
  /** 3: la lista dei tornei non ha risposto entro `NO_ANSWER_MS`. */
  | { kind: 'no-answer' }
  /** 3 con un errore vero: la lista e' tornata rifiutata. `LimuloDegraded` lo racconta. */
  | { kind: 'failed'; error: unknown }
  /**
   * 4: **non posso chiedere.** `/v1/games` non risponde (errore o silenzio), oppure risponde e
   * nessun gioco ha il `profileType` di questo gioco: senza il catalogo il filtro non ha di che
   * scegliere, e lo zero che ne uscirebbe non sarebbe «nessun torneo».
   */
  | { kind: 'cannot-ask' }
  /**
   * 5: il server ha parlato, e di questo gioco non c'e' niente **sulle pagine caricate**.
   * `incomplete` = c'e' un'altra pagina: in quel caso NON e' ancora un «nessun torneo», e chi
   * rende offre il seguito invece di affermare il vuoto.
   */
  | { kind: 'empty'; incomplete: boolean }
  | { kind: 'ok' };

export interface UseTorneiDelGioco {
  state: TorneiDelGioco;
  tournaments: LimuloTournament[];
  /** Il torneo della striscia (v. `featuredTournamentOf`); `null` se non ce n'e'. */
  featured: LimuloTournament | null;
  /** «Le mie iscrizioni» come insieme di id; `null` = non sono arrivate (non «nessuna»). */
  mineIds: ReadonlySet<string> | null;
  /** C'e' un'altra pagina della lista Limulo da chiedere (il filtro e' lato client). */
  canLoadMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  /** Il torneo in evidenza e' uno dei miei. */
  featuredIsMine: boolean;
  /**
   * Il `gameId` del gioco nel catalogo (quello della lista, `?gameId=`); `null` se non si puo' chiedere. Le quattro tab lo
   * usano per «I miei» del gioco (I-346: dentro la sezione di un gioco anche le mie sono di quel gioco).
   */
  gameId: string | null;
}

/**
 * I tornei di UN gioco nella sua lobby: la lista pubblica Limulo, filtrata sul catalogo giochi. Era `useBackgammonTournaments`
 * (che c'e' ancora, un filo sopra questo: `useBackgammonTournaments.ts`); l'ha estratto per `profileType` P2 degli scacchi su Limulo
 * (E6 di ADR-0193), perche' la scheda «Tornei» di un altro gioco non riscriva i cinque vuoti. `gioco` e' l'id del gioco NELLA TMA
 * (`BACKGAMMON_GAME_ID`, `CHESS_GAME_ID`: `lib/gameFeatures.ts`), non il `gameId` del catalogo: il ponte e' `PROFILE_TYPE_DEL_GIOCO`
 * (`lib/limulo/giocoSelezionato.ts`), UNA riga per gioco.
 *
 * ⚠️ **`skip`** e' la serratura: la sezione Limulo sta dietro `VITE_FEATURE_LIMULO_ENABLED`, e
 * con il flag spento questo hook NON deve fare nessuna chiamata — le due lobby (rotta e Hub)
 * decidono se montarlo, ma una chiamata partita per sbaglio e' un accesso che il flag doveva
 * impedire.
 *
 * ⛔ **Il filtro e' lato client, e la lista e' paginata dal server**: una pagina puo' non
 * contenere nessun torneo di backgammon e la seguente si'. Per questo «vuoto» (5) si dichiara
 * solo quando NON c'e' un'altra pagina; altrimenti `canLoadMore` resta acceso e chi rende offre
 * il seguito. Dire «nessun torneo» su una lista che si sa incompleta e' il vuoto affermato.
 */
export function useTorneiDelGioco({ gioco, skip = false }: { gioco: number; skip?: boolean }): UseTorneiDelGioco {
  const [page, setPage] = useState(1);
  /**
   * 🔑 La lobby si rilegge col ritmo delle liste Limulo (2026-09-24) — stessa voce di cache della
   * lista tornei (`serializeQueryArgs` la tiene unica), quindi stesse regole: solo sulla PRIMA
   * pagina, per la ragione scritta in `LimuloTournaments`. «I miei» segue lo stesso ritmo: e'
   * cio' che decide il torneo in evidenza. Il catalogo giochi no: non cambia mentre lo si guarda.
   */
  const polling = useLimuloPolling();
  const gq = useGetLimuloGamesQuery(undefined, { skip });
  /*
   * 🔴 I-342 (04/10): la lista si chiede GIA' FILTRATA dal server (`?gameId=` del backgammon), con la stessa regola
   * della lista Tornei (`listaDelGioco`, `lib/limulo/giocoSelezionato.ts`): prima il catalogo, poi la lista del suo
   * `gameId`. Prima si leggeva la lista di TUTTI i giochi e si filtrava qui: su una pagina da 50 righe miste il
   * backgammon ne prendeva una parte, e «Carica altri» girava su pagine quasi vuote. Senza un `gameId` solo (catalogo
   * senza backgammon, o con piu' id) la lista non si chiede: e' il caso 4, come prima.
   */
  const lista = listaDelGioco({
    activeGameId: gioco,
    nomeSelettore: null,
    catalogo: gq.data,
    catalogoInErrore: gq.data === undefined && gq.error !== undefined,
  });
  const tipo = profileTypeDelGioco(gioco);
  const bgGameId = lista.kind === 'gioco' ? lista.gameId : null;
  const tq = useGetLimuloTournamentsQuery(
    skip || bgGameId === null ? skipToken : { page, gameId: bgGameId },
    polling.ritmo,
  );
  /*
   * 🔑 «I miei» del BACKGAMMON, con gli STESSI argomenti delle quattro tab (`TorneiQuattroTab`: `{ gameId }` del catalogo),
   * quindi la STESSA voce di cache: nella scheda «Tornei» del BG una lettura di `/mine` per giro, non due (misura dal vivo di
   * consegna-tornei-orcb, 04/10 15:11-15:14Z: c'erano `/mine?gameId=100` e `/mine` senza filtro). Il torneo in evidenza e' del
   * backgammon (`featuredTournamentOf` sui soli tornei BG), quindi le mie degli altri giochi non gli servivano.
   * ⚠️ A catalogo non letto (nessun `gameId`) niente lettura: mai `/mine` di tutti i giochi come ripiego. `mineIds` resta `null`
   * («non sono arrivate»), come prima finche' la risposta non c'era.
   */
  const mq = useGetMyLimuloTournamentsQuery(skip || bgGameId === null ? skipToken : { gameId: bgGameId }, polling.ritmo);
  useLimuloPollingGuida(polling, tq, page === 1 ? LIMULO_POLL_LISTA_MS : 0);

  // La lista non chiesta non e' un'attesa: niente timer finche' il catalogo non ha dato il `gameId`.
  const pendingT = bgGameId !== null && tq.data === undefined && !tq.error;
  const pendingG = gq.data === undefined && !gq.error;
  const slowT = useStalled(!skip && pendingT);
  const slowG = useStalled(!skip && pendingG);
  const noAnswerT = useStalled(!skip && pendingT, NO_ANSWER_MS);
  const noAnswerG = useStalled(!skip && pendingG, NO_ANSWER_MS);

  const mineIds = useMemo<ReadonlySet<string> | null>(() => {
    if (mq.data === undefined) return null;
    const ids = new Set<string>();
    for (const t of mq.data) {
      if (t.id) ids.add(t.id);
      if (t.tournamentId) ids.add(t.tournamentId);
    }
    return ids;
  }, [mq.data]);

  const filtered = useMemo(() => {
    if (gq.data === undefined) return null;
    // Catalogo senza il gioco, o con piu' `gameId` per il suo `profileType`: la lista non si e' chiesta ⇒ caso 4.
    if (lista.kind !== 'gioco' || tipo === null) return { kind: 'no-catalog' as const };
    if (tq.data === undefined) return null;
    const now = Date.now();
    // I-207/L-2362 (a): la lobby non e' la lista Sfide — un torneo nato da una sfida di
    // piattaforma (`origine: 'sfida'`, oggi ancora backgammon-only in T1, domani anche T2) non ci
    // compare (revisione #487 punto 4, `nonEUnaSfida`, stessa regola di `LimuloTournaments.tsx`).
    const pubblicati = tq.data.items.filter((t) => isPublished(t, now) && nonEUnaSfida(t));
    const f = torneiDelTipoOf(pubblicati, gq.data, tipo);
    return f.kind === 'ok' ? { ...f, tournaments: sortTournaments<LimuloTournament>(f.tournaments, now) } : f;
  }, [tq.data, gq.data, lista.kind, tipo]);

  const altrePagine =
    tq.data !== undefined &&
    hasNextPage({
      page,
      limit: LIMULO_PAGE_SIZE,
      // ⚠️ L'ULTIMA pagina, non il totale accumulato (v. `LimuloTournaments`).
      received: tq.data.lastPageCount,
      totalPages: tq.data.pagination?.totalPages ?? null,
    });

  const tournaments = filtered?.kind === 'ok' ? filtered.tournaments : [];
  const featured = featuredTournamentOf(tournaments, mineIds);

  let state: TorneiDelGioco;
  // ⚠️ `failed` solo se la lista non e' MAI arrivata: sotto polling un giro fallito mette un
  // `error` accanto a una lista valida, e la lobby lampeggerebbe fra i tornei e il guasto.
  if (tq.error && tq.data === undefined) state = { kind: 'failed', error: tq.error };
  else if (gq.error) state = { kind: 'cannot-ask' };
  else if (noAnswerT) state = { kind: 'no-answer' };
  else if (noAnswerG) state = { kind: 'cannot-ask' };
  else if (pendingT || pendingG || filtered === null) state = { kind: 'waiting', slow: slowT || slowG };
  else if (filtered.kind === 'no-catalog') state = { kind: 'cannot-ask' };
  else if (filtered.tournaments.length === 0) state = { kind: 'empty', incomplete: altrePagine };
  else state = { kind: 'ok' };

  return {
    state,
    tournaments,
    featured,
    mineIds,
    canLoadMore: altrePagine,
    loadingMore: tq.isFetching && page > 1,
    loadMore: () => setPage((p) => p + 1),
    featuredIsMine: featured !== null && isMineTournament(featured, mineIds),
    gameId: bgGameId,
  };
}
